import { highlight, languages } from 'prismjs/components/prism-core';
import { useState, useEffect, useRef } from 'react';
import 'prismjs/components/prism-json';
import 'prismjs/themes/prism.css';
import Editor from 'react-simple-code-editor';

import NoConfigPlaceholder from './NoConfigPlaceholder.tsx';
import type { RecordType } from './types.ts';
import { isValidConfig } from './types.ts';

interface ConfigJsonPanelProps {
    recordType: RecordType;
    config: any;
    onConfigChange: (config: any) => void;
}

const ConfigJsonPanel = ({ recordType, config, onConfigChange }: ConfigJsonPanelProps) => {
    const hasConfig = isValidConfig(config);
    const [jsonError, setJsonError] = useState<string | null>(null);
    const [localJsonValue, setLocalJsonValue] = useState(() => JSON.stringify(config, null, 2));
    const isEditingRef = useRef(false);

    // Sync local JSON value with config when not actively editing, or reset on recordType change
    useEffect(() => {
        if (!isEditingRef.current) {
            setLocalJsonValue(JSON.stringify(config, null, 2));
            setJsonError(null);
        }
    }, [config, recordType]);

    // Reset editing state when switching record types
    useEffect(() => {
        isEditingRef.current = false;
    }, [recordType]);

    const handleJsonChange = (value: string) => {
        isEditingRef.current = true;
        setLocalJsonValue(value);
        try {
            const parsed = JSON.parse(value);
            setJsonError(null);
            onConfigChange(parsed);
        } catch (e) {
            setJsonError((e as Error).message);
        }
    };

    const handleJsonBlur = () => {
        isEditingRef.current = false;
        // If there's no error, sync the formatted JSON
        if (!jsonError) {
            setLocalJsonValue(JSON.stringify(config, null, 2));
        }
    };

    if (!hasConfig) {
        return <NoConfigPlaceholder />;
    }

    return (
        <div className="flex flex-col flex-1 h-full">
            <div className="flex-1 min-h-0 overflow-auto p-4 bg-surface-weak">
                <div className="bg-surface-weak border border-line-weak rounded overflow-hidden">
                    <Editor
                        className="text-xs font-mono"
                        value={localJsonValue}
                        onValueChange={handleJsonChange}
                        highlight={(code: string) => highlight(code, languages.json)}
                        padding={12}
                        onBlur={handleJsonBlur}
                    />
                </div>
                {jsonError && (
                    <div className="mt-2 px-3 py-2 text-xs text-status-danger-medium bg-status-danger-weak rounded">
                        {jsonError}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ConfigJsonPanel;
